import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation } from "wouter";
import {
  Bot, Users, LogOut, Sparkles, Activity, Send, Clock, IndianRupee,
  Sun, Moon, Mail, MessageCircle, Grid3x3, Search, ChevronDown, X, Rocket, Gauge, FileSearch,
} from "lucide-react";
import { PortalSwitcher } from "@/components/portal-switcher";
import { CohortProvider } from "@/lib/cohort-context";
import { CohortSwitcher } from "@/components/cohort-switcher";
import { useThemeMode } from "@/lib/use-theme-mode";

type NavCounts = {
  team?: number;
  agents?: number;
  viren?: number;
  originations?: number;
  outreach?: number;
  health?: number;
};

// Each "app" the admin can open. Grouped by category so the launcher
// stays scannable as we keep adding tools. Keep descriptions short — one
// line, plain English, no marketing language.
type AppDef = {
  href: string;
  name: string;
  desc: string;
  icon: any;
  category: string;
  countKey?: keyof NavCounts;
  danger?: boolean;
};

const APPS: AppDef[] = [
  // Investor flow
  { href: "/admin/investors", name: "Investors", desc: "Master investor list, allocations, totals invested.", icon: Users, category: "Investor" },
  { href: "/admin/conversations", name: "Convos", desc: "Live view of every investor's chat with Kavya.", icon: MessageCircle, category: "Investor" },
  { href: "/admin/outreach", name: "Outreach", desc: "Cold outreach approvals and pending message queue.", icon: Send, category: "Investor", countKey: "outreach" },
  { href: "/admin/email", name: "Email", desc: "Assistant, approvals, schedule, people and reports for all outgoing email.", icon: Mail, category: "Investor" },
  { href: "/admin/research-pipeline", name: "Research Pipeline", desc: "Private PIM drafting, cohort access, approvals, and campaigns.", icon: FileSearch, category: "Investor" },
  { href: "/admin/bulletins", name: "Bulletins", desc: "Daily evening digest sent to the operator team.", icon: Mail, category: "Investor" },
  { href: "/admin/onboarding-drips", name: "Drips", desc: "Per-investor onboarding email progress and pacing.", icon: Mail, category: "Investor" },
  // Origination
  { href: "/admin/originations", name: "Originations", desc: "Top-of-funnel sourcing and screening.", icon: Sparkles, category: "Origination", countKey: "originations" },
  { href: "/admin/secondary", name: "Secondary", desc: "Secondary deals and ROFR pipeline.", icon: IndianRupee, category: "Origination" },
  // Ops
  { href: "/admin/team", name: "Team", desc: "Operator and admin user management.", icon: Users, category: "Ops", countKey: "team" },
  { href: "/admin/health", name: "Health", desc: "Red flags, system health, scheduler status.", icon: Activity, category: "Ops", countKey: "health", danger: true },
  { href: "/admin/advisor-health", name: "Advisor Health", desc: "Per-advisor coverage, last-touch drift, open escalations.", icon: Activity, category: "Ops" },
  { href: "/admin/spend", name: "Spend", desc: "Cloud & AI spend meter, budget breaker, risk alerts.", icon: Gauge, category: "Ops" },
  { href: "/admin/timestamp-trail", name: "Trail", desc: "Audit log of every admin action with full history.", icon: Clock, category: "Ops" },
  // Founder OS
  { href: "/admin/founder-portal", name: "Founder OS", desc: "Reply in threads, publish claims, approve weekly updates.", icon: Rocket, category: "Founder OS" },
  // AI agents
  { href: "/admin/agents", name: "Agents", desc: "All AI agents (Kavya, Meera, Drishti, and others).", icon: Bot, category: "AI", countKey: "agents" },
  { href: "/admin/viren", name: "Viren", desc: "Founder escalation agent and queue.", icon: Sparkles, category: "AI", countKey: "viren" },
  { href: "/admin/rateek", name: "Rateek", desc: "Platform features intelligence and bites.", icon: Sparkles, category: "AI" },
];

const CATEGORY_ORDER = ["Investor", "Origination", "Founder OS", "Ops", "AI"] as const;

// Tiny title-case helper for the active-app label in the launcher button.
function findActiveApp(location: string): AppDef | null {
  // longest-prefix match so /admin/campaigns/123 still resolves to Campaigns
  let best: AppDef | null = null;
  for (const app of APPS) {
    if (location === app.href || location.startsWith(app.href + "/")) {
      if (!best || app.href.length > best.href.length) best = app;
    }
  }
  return best;
}

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const [adminUser, setAdminUser] = useState<{ name: string; email: string; role: string } | null>(null);
  const [counts, setCounts] = useState<NavCounts>({});
  // Admin layout defaults to LIGHT — admin staff work in office light all day,
  // and prior to this the layout had no theme attribute at all so child pages
  // (which use hardcoded `bg-[#0A0A0A]` / `text-white`) were stuck in dark
  // regardless of preference. The shared `[data-admin-theme="light"]` CSS
  // overrides in index.css now remap those hardcoded utilities to light.
  const { mode: theme, toggle: toggleTheme } = useThemeMode("admin", "light");
  const ThemeIcon = theme === "dark" ? Sun : Moon;

  // Apps launcher state
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [appFilter, setAppFilter] = useState("");
  const launcherRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("admin_user");
      if (stored) setAdminUser(JSON.parse(stored));
    } catch {}
  }, []);

  // Poll the admin nav-counts endpoint so the pills reflect work that piles
  // up while the admin is on another tab. 60s is a deliberate floor — these
  // pills should not dictate scroll-by-scroll attention, only catch arrivals
  // the admin missed. We also pull /admin/health in parallel so we can show a
  // red-flag count on the Health tab without duplicating flag-derivation logic
  // on the server. Failures are silent: a bad fetch should not break the
  // chrome of every admin page.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem("admin_token");
        if (!token) return;
        const headers = { "x-admin-token": token };
        const [navRes, healthRes] = await Promise.all([
          fetch("/api/admin/nav-counts", { headers }),
          fetch("/api/admin/health", { headers }),
        ]);
        const next: NavCounts = {};
        if (navRes.ok) Object.assign(next, await navRes.json());
        if (healthRes.ok) {
          const h = await healthRes.json();
          const flags = Array.isArray(h?.flags) ? h.flags : [];
          next.health = flags.filter((f: any) => f?.severity === "red").length;
        }
        if (!cancelled) setCounts(next);
      } catch {
        /* swallow — nav badges are decoration, not critical path */
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Close launcher on outside click + Esc
  useEffect(() => {
    if (!launcherOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!launcherRef.current) return;
      if (!launcherRef.current.contains(e.target as Node)) setLauncherOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setLauncherOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [launcherOpen]);

  // Close launcher whenever the route changes (i.e. user picked a tile).
  useEffect(() => { setLauncherOpen(false); setAppFilter(""); }, [location]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    window.location.replace("/admin/login");
  };

  const activeApp = findActiveApp(location);
  const redFlagCount = counts.health || 0;

  // Total non-danger pending across all apps — surfaces a single dot on the
  // Apps button so the operator notices new work even while on another page.
  const totalPending = useMemo(() => {
    return APPS.reduce((sum, app) => {
      if (app.danger) return sum;
      const c = app.countKey ? (counts[app.countKey] || 0) : 0;
      return sum + c;
    }, 0);
  }, [counts]);

  const filteredApps = useMemo(() => {
    const q = appFilter.trim().toLowerCase();
    if (!q) return APPS;
    return APPS.filter((a) =>
      a.name.toLowerCase().includes(q) ||
      a.desc.toLowerCase().includes(q) ||
      a.category.toLowerCase().includes(q),
    );
  }, [appFilter]);

  // Group filtered apps by category, preserving CATEGORY_ORDER.
  const groupedApps = useMemo(() => {
    const map = new Map<string, AppDef[]>();
    for (const a of filteredApps) {
      const arr = map.get(a.category) || [];
      arr.push(a);
      map.set(a.category, arr);
    }
    return CATEGORY_ORDER.filter((c) => map.has(c)).map((c) => ({ category: c, apps: map.get(c)! }));
  }, [filteredApps]);

  return (
    <CohortProvider identity="admin">
      <div data-admin-theme={theme} className={`min-h-screen w-full bg-background ${theme === "dark" ? "dark" : ""}`}>
        <header className="border-b bg-background relative">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex flex-wrap sm:flex-nowrap items-center justify-between gap-4">
            <Link href="/admin">
              <div className="flex items-center gap-2 cursor-pointer">
                <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground font-bold text-sm">F</span>
                </div>
                <div>
                  <h1 className="text-lg font-sans tracking-tight font-bold">Favcy</h1>
                  <p className="text-muted-foreground/50 text-[10px] font-mono uppercase tracking-[0.2em] leading-none">Favcy Select</p>
                </div>
              </div>
            </Link>

            <div className="flex flex-wrap sm:flex-nowrap min-w-0 w-full sm:w-auto items-center gap-2 sm:flex-1 justify-start sm:justify-end">
              {/* Apps launcher button */}
              <div ref={launcherRef} className="relative">
                <button
                  onClick={() => setLauncherOpen((v) => !v)}
                  aria-expanded={launcherOpen}
                  aria-haspopup="dialog"
                  aria-label="Open apps launcher"
                  data-testid="button-apps-launcher"
                  className={`relative inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-border transition-colors ${
                    launcherOpen ? "bg-muted" : "hover:bg-muted"
                  }`}
                >
                  <Grid3x3 className="w-4 h-4 text-[#FF5C00]" />
                  <span className="hidden sm:inline">Apps</span>
                  {activeApp && (
                    <span className="hidden md:inline text-muted-foreground">
                      <span className="mx-1">/</span>
                      <span className="text-foreground">{activeApp.name}</span>
                    </span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${launcherOpen ? "rotate-180" : ""}`} />
                  {/* Indicators: red dot for red flags, orange dot for any pending */}
                  {redFlagCount > 0 && (
                    <span
                      title={`${redFlagCount} health red flag${redFlagCount === 1 ? "" : "s"}`}
                      className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold leading-none bg-red-600 text-white">
                      {redFlagCount > 99 ? "99+" : redFlagCount}
                    </span>
                  )}
                  {redFlagCount === 0 && totalPending > 0 && (
                    <span
                      title={`${totalPending} pending across apps`}
                      className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold leading-none bg-[#FF5C00] text-white">
                      {totalPending > 99 ? "99+" : totalPending}
                    </span>
                  )}
                </button>

                {launcherOpen && (
                  <div className="absolute right-0 top-full mt-2 w-[min(720px,calc(100vw-3rem))] bg-background border border-border rounded-lg shadow-2xl z-50 max-h-[80vh] overflow-hidden flex flex-col">
                    <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                      <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                      <input
                        autoFocus
                        value={appFilter}
                        onChange={(e) => setAppFilter(e.target.value)}
                        placeholder="Search apps"
                        className="flex-1 bg-transparent outline-none text-sm" />
                      <button
                        onClick={() => setLauncherOpen(false)}
                        className="p-1 rounded-md hover:bg-muted"
                        aria-label="Close apps launcher">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="overflow-y-auto p-4 space-y-5">
                      {groupedApps.length === 0 && (
                        <div className="text-center text-sm text-muted-foreground py-6">No apps match "{appFilter}".</div>
                      )}
                      {groupedApps.map(({ category, apps }) => (
                        <div key={category}>
                          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">{category}</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {apps.map((app) => {
                              const Icon = app.icon;
                              const isActive = activeApp?.href === app.href;
                              const badge = app.countKey ? (counts[app.countKey] || 0) : 0;
                              const isDanger = app.danger;
                              return (
                                <Link
                                  key={app.href}
                                  href={app.href}
                                  className={`group flex items-start gap-3 p-3 rounded-md border transition-colors ${
                                    isActive
                                      ? "border-[#FF5C00] bg-[#FF5C00]/5"
                                      : "border-border hover:border-[#FF5C00]/50 hover:bg-muted/50"
                                  }`}
                                >
                                  <div className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center ${
                                    isActive ? "bg-[#FF5C00] text-white" : "bg-muted text-foreground/70 group-hover:text-[#FF5C00]"
                                  }`}>
                                    <Icon className="w-4.5 h-4.5" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-sm font-semibold truncate">{app.name}</span>
                                      {badge > 0 && (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold leading-none ${
                                          isDanger ? "bg-red-600 text-white" : "bg-[#FF5C00] text-white"
                                        }`}>
                                          {badge > 99 ? "99+" : badge}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-muted-foreground leading-snug mt-0.5 line-clamp-2">{app.desc}</p>
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <CohortSwitcher tone={theme} />
              <button
                onClick={toggleTheme}
                title={`Switch to ${theme === "dark" ? "day" : "night"} mode`}
                aria-label="Toggle theme"
                className="p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                data-testid="button-theme-toggle"
              >
                <ThemeIcon className="w-4 h-4" />
              </button>
              {adminUser && (
                <div className="flex items-center gap-3 pl-3 border-l border-border">
                  <div className="text-right hidden sm:block">
                    <p className="text-xs font-medium">{adminUser.name}</p>
                    <p className="text-[10px] text-muted-foreground">{adminUser.role === "master_admin" ? "Master Admin" : "Operator"}</p>
                  </div>
                  <button onClick={handleLogout} className="p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors" title="Sign out">
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-6 md:px-10 py-8">
          {children}
        </main>
        <footer className="border-t mt-8 py-5">
          <PortalSwitcher current="admin" tone="light" />
        </footer>
      </div>
    </CohortProvider>
  );
}
